import { ChevronUp } from "lucide-react";
import { type ReactNode, useState } from "react";

import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../api/use-claim";
import { type ClaimTypeName, isCancellationLikeClaimType } from "../lib/claim-types";
import { FlightOverviewHeader } from "./flight-overview-header";
import { FlightOverviewItinerary } from "./flight-overview-itinerary";
import { FlightOverviewSchedule } from "./flight-overview-schedule";

export type OverviewFlight = ClaimWithRelations["legalSpecification"]["effectiveFlight"];
export type EffectiveAirline = ClaimWithRelations["legalSpecification"]["effectiveAirline"];
export type ReroutingProposals =
  ClaimWithRelations["legalSpecification"]["impactedSegment"]["reroutingProposals"];

/**
 * Instant show/hide disclosure mirroring headlessui's Disclosure in
 * FlightOverview.vue — plain state + conditional render, no animation.
 */
function ReroutingDisclosure({ label, children }: { label: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full justify-between rounded-lg border bg-white px-4 py-2 text-left text-sm text-slate-900 shadow-md hover:bg-gray-100"
      >
        {label}
        <ChevronUp className={cn("h-5 w-5 text-slate-500", open && "rotate-180 transform")} />
      </button>
      {open ? (
        <div className="divide-y-8 divide-transparent px-4 pt-4 pb-2 text-left text-sm">{children}</div>
      ) : null}
    </div>
  );
}

interface FlightOverviewProps {
  claimId: string;
  airline: EffectiveAirline;
  flight: OverviewFlight;
  claimTypeName: ClaimTypeName | null;
  reroutingProposals: ReroutingProposals;
  actualRerouting: unknown;
  cancelledBy?: string | null;
  cancelledIndicator?: "RECORD" | "SEGMENT" | null;
  cancelledSignature?: string | null;
  cancelledDate?: Date | null;
}

/**
 * Port of FlightOverview.vue.
 * TODO: FlightDetailModal (détail Amadeus GDS) — the badge tooltip in the
 * header already surfaces the enrichment fields.
 */
export function FlightOverview(props: FlightOverviewProps) {
  const { flight, claimTypeName, reroutingProposals } = props;
  const displayUTC = false;

  const marketingAirline = {
    id: flight.airlineId,
    name: flight.airline.name,
    icao: flight.airline.icao,
    iconURL: flight.airline.legalData?.iconURL || null,
    companyId: flight.airline.legalData?.companyId || null,
  };

  const operatingAirline =
    !flight.operatingAirlineId || flight.operatingAirlineId === flight.airlineId
      ? null
      : {
          id: flight.operatingAirlineId,
          name: flight.operatingAirline?.name || null,
          iconURL: flight.operatingAirline?.legalData?.iconURL || null,
          companyId: flight.operatingAirline?.legalData?.companyId || null,
        };

  return (
    <div className="mx-2 my-4 flex flex-col rounded-md border bg-white py-2 shadow">
      <div className="overflow-hidden border-b-2 bg-white text-left">
        <FlightOverviewHeader
          claimId={props.claimId}
          flightId={flight.id}
          marketingAirlineId={marketingAirline.id}
          marketingAirlineName={marketingAirline.name}
          marketingAirlineIcao={marketingAirline.icao}
          marketingAirlineIconUrl={marketingAirline.iconURL}
          marketingAirlineCompanyId={marketingAirline.companyId}
          operatingAirlineId={operatingAirline?.id || null}
          operatingAirlineName={operatingAirline?.name || null}
          operatingAirlineIconUrl={operatingAirline?.iconURL || null}
          operatingAirlineCompanyId={operatingAirline?.companyId || null}
          gdsMonitoringData={flight.gdsMonitoringData}
          claimTypeName={claimTypeName}
          flightIdentifier={flight.flightIdentifier}
          scheduledDepartureLocal={flight.scheduledDepartureLocal}
          cancelledBy={props.cancelledBy}
          cancelledIndicator={props.cancelledIndicator}
          cancelledSignature={props.cancelledSignature}
          cancelledDate={props.cancelledDate}
        />
      </div>

      <div>
        <FlightOverviewItinerary
          claimTypeName={claimTypeName}
          scheduledDepartureUTC={new Date(flight.scheduledDepartureUTC)}
          scheduledArrivalUTC={new Date(flight.scheduledArrivalUTC)}
          departureAirport={flight.departureAirport}
          arrivalAirport={flight.arrivalAirport}
          utc={displayUTC}
        />
        <FlightOverviewSchedule
          claimTypeName={claimTypeName}
          scheduledDepartureUTC={new Date(flight.scheduledDepartureUTC)}
          scheduledArrivalUTC={new Date(flight.scheduledArrivalUTC)}
          actualDepartureUTC={flight.actualDepartureUTC ? new Date(flight.actualDepartureUTC) : null}
          inGateArrivalUTC={flight.inGateArrivalUTC ? new Date(flight.inGateArrivalUTC) : null}
          departureAirport={flight.departureAirport}
          arrivalAirport={flight.arrivalAirport}
          utc={displayUTC}
          monitoringStatus={flight.monitoringStatus || null}
        />
      </div>

      {reroutingProposals?.length ? (
        <div
          className={`w-full pt-2 ${
            isCancellationLikeClaimType(claimTypeName)
              ? "border-l-2 border-red-600"
              : claimTypeName
                ? "border-l-2 border-amber-500"
                : "border-l-2 border-blue-500"
          }`}
        >
          <div className="mx-auto w-full rounded-2xl p-2">
            {reroutingProposals.map((rerouting, idx) => (
              <ReroutingDisclosure
                key={rerouting.id}
                label={
                  <span>
                    Voir la proposition de réacheminement{" "}
                    {reroutingProposals.length > 1 ? <span>#{idx + 1}</span> : null}
                  </span>
                }
              >
                {rerouting.reroutingGroup?.reroutings?.map(({ proposedSegment }, i) => (
                  <div key={proposedSegment.flight.id}>
                    {i > 0 ? <p className="flex py-2 font-bold">Escale numéro {i + 1}</p> : null}
                    <div className="overflow-hidden border-y-2 bg-white text-left">
                      <FlightOverviewHeader
                        claimId={props.claimId}
                        flightId={proposedSegment.flight.id}
                        marketingAirlineId={proposedSegment.flight.airlineId}
                        marketingAirlineName={proposedSegment.flight.airline.name}
                        marketingAirlineIcao={proposedSegment.flight.airline.icao}
                        marketingAirlineIconUrl={proposedSegment.flight.airline.legalData?.iconURL || null}
                        marketingAirlineCompanyId={
                          proposedSegment.flight.airline.legalData?.companyId || null
                        }
                        operatingAirlineId={
                          proposedSegment.flight.operatingAirlineId &&
                          proposedSegment.flight.operatingAirlineId !== proposedSegment.flight.airlineId
                            ? proposedSegment.flight.operatingAirlineId
                            : null
                        }
                        operatingAirlineName={proposedSegment.flight.operatingAirline?.name || null}
                        operatingAirlineIconUrl={
                          proposedSegment.flight.operatingAirline?.legalData?.iconURL || null
                        }
                        operatingAirlineCompanyId={
                          proposedSegment.flight.operatingAirline?.legalData?.companyId || null
                        }
                        claimTypeName={null}
                        flightIdentifier={proposedSegment.flight.flightIdentifier}
                        scheduledDepartureLocal={proposedSegment.flight.scheduledDepartureLocal}
                      />
                    </div>
                    <FlightOverviewItinerary
                      claimTypeName={null}
                      scheduledDepartureUTC={new Date(proposedSegment.flight.scheduledDepartureUTC)}
                      scheduledArrivalUTC={new Date(proposedSegment.flight.scheduledArrivalUTC)}
                      departureAirport={proposedSegment.flight.departureAirport}
                      arrivalAirport={proposedSegment.flight.arrivalAirport}
                      utc={displayUTC}
                    />
                    <FlightOverviewSchedule
                      claimTypeName={null}
                      monitoringStatus={proposedSegment.flight.monitoringStatus || null}
                      scheduledDepartureUTC={new Date(proposedSegment.flight.scheduledDepartureUTC)}
                      scheduledArrivalUTC={new Date(proposedSegment.flight.scheduledArrivalUTC)}
                      actualDepartureUTC={
                        proposedSegment.flight.actualDepartureUTC
                          ? new Date(proposedSegment.flight.actualDepartureUTC)
                          : null
                      }
                      inGateArrivalUTC={
                        proposedSegment.flight.inGateArrivalUTC
                          ? new Date(proposedSegment.flight.inGateArrivalUTC)
                          : null
                      }
                      departureAirport={proposedSegment.flight.departureAirport}
                      arrivalAirport={proposedSegment.flight.arrivalAirport}
                      utc={displayUTC}
                    />
                  </div>
                ))}
              </ReroutingDisclosure>
            ))}
          </div>
        </div>
      ) : isCancellationLikeClaimType(claimTypeName) ? (
        <div className="w-full border-l-2 border-red-600">
          <div className="mx-auto w-full rounded-2xl p-2">
            <div className="mb-2">
              <div className="flex w-full cursor-default justify-between rounded-lg border px-4 py-2 text-left text-sm font-normal text-slate-900 shadow">
                <p>Aucune proposition de réacheminement</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
